import { useFeatureIsOn } from "@growthbook/growthbook-react";
import {
  Box,
  CircularProgress,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Alert,
  Button,
  SectionHeader,
  Typography,
} from "@platform/design-system";
import {
  CONSULTANT_STEWARDSHIP_PENDING_PREMIUM_FALLBACK,
  CONSULTANT_STEWARDSHIP_REPORTING,
  REMOVE_IGO,
} from "@platform/feature-flags";
import { format, isValid } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { Loader } from "../../components";
import { ROUTE_NAMES } from "../../constants";
import { extractErrorMessage } from "../../utils";

import { CARD_SX } from "./stewardship/constants";
import {
  useBrokerStewardshipQuery,
  type StewardshipDateBasis,
} from "./useBrokerStewardship";
import { useConsultingBroker } from "./useConsultingBroker";
import { useExportStewardship } from "./stewardship/export/useExportStewardship";
import { useStewardshipPowerPointDownload } from "./useStewardshipPowerPointDownload";
import { CarrierOutcomesCard } from "./stewardship/CarrierOutcomesCard";
import { CarrierPerformanceCard } from "./stewardship/CarrierPerformanceCard";
import { KeyMetricsCard } from "./stewardship/KeyMetricsCard";
import {
  MarketingOutcomesCard,
  type StrategicTierOutcome,
} from "./stewardship/MarketingOutcomesCard";
import { PendingOutcomesCard } from "./stewardship/PendingOutcomesCard";
import { RfpSubmitterActivityCard } from "./stewardship/RfpSubmitterActivityCard";
import { YtdSummaryCard } from "./stewardship/YtdSummaryCard";

import type { CarrierTier, ConsultingBrokerStewardshipResponse } from "./types";

const KNOWN_CARRIER_TIERS: CarrierTier[] = [
  "Tier 1",
  "Tier 2",
  "Non-Strategic",
  "Mixed",
  "Unknown",
];

const DATE_PICKER_SLOT_PROPS = {
  textField: { size: "small" as const },
  day: {
    sx: {
      color: "text.primary",
      "&:hover, &:focus": {
        bgcolor: "surfaces.primary",
      },
      "&.MuiPickersDay-today": {
        borderColor: "brandSupporting.medium",
        color: "text.primary",
      },
      "&.Mui-selected": {
        bgcolor: "brandSupporting.dark",
        color: "common.white",
        "&:hover, &:focus": {
          bgcolor: "brandSupporting.dark",
        },
      },
      "&.Mui-disabled, &.MuiPickersDay-dayOutsideMonth": {
        color: "text.disabled",
      },
    },
  },
};

function normalizeCarrierTier(tier: string | undefined): CarrierTier {
  return KNOWN_CARRIER_TIERS.includes(tier as CarrierTier)
    ? (tier as CarrierTier)
    : "Unknown";
}

type StrategicSuccessMetrics = {
  success_percent: number;
  tier_1_premium: number;
  tier_2_premium: number;
  non_strategic_premium: number;
  opportunity_premium: number;
};

function marketingOutcomeFromSuccess(
  success: StrategicSuccessMetrics | undefined,
): StrategicTierOutcome {
  return {
    total: success?.opportunity_premium ?? 0,
    tier1: success?.tier_1_premium ?? 0,
    tier2: success?.tier_2_premium ?? 0,
    nonStrategic: success?.non_strategic_premium ?? 0,
    successPercent: success?.success_percent ?? 0,
  };
}

function isValidIsoDate(value: string | null): value is string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
  if (!match) {
    return false;
  }

  const [, yearPart, monthPart, dayPart] = match;
  const year = Number(yearPart);
  const month = Number(monthPart);
  const day = Number(dayPart);
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function parseIsoDate(value: string | null | undefined): Date | null {
  const isoValue = value ?? null;

  if (!isValidIsoDate(isoValue)) {
    return null;
  }

  const [year, month, day] = isoValue.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function formatIsoDate(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

function formatAcceptedDate(date: Date | null): string | null {
  return date && isValid(date) ? formatIsoDate(date) : null;
}

function formatPeriodLabel(
  response: ConsultingBrokerStewardshipResponse,
): string {
  const startDate = new Date(`${response.period.start_date}T00:00:00`);
  const endDate = new Date(`${response.period.end_date}T00:00:00`);

  return `${startDate.toLocaleDateString("en-US", { month: "short", year: "numeric" })} – ${endDate.toLocaleDateString("en-US", { month: "short", year: "numeric" })}`;
}

function updateDateRangeSearchParam({
  setSearchParams,
  key,
  value,
  fallbackStartDate,
  fallbackEndDate,
}: {
  setSearchParams: ReturnType<typeof useSearchParams>[1];
  key: "start_date" | "end_date";
  value: string | null;
  fallbackStartDate: string;
  fallbackEndDate: string;
}): void {
  setSearchParams((currentParams) => {
    const nextParams = new URLSearchParams(currentParams);

    if (!isValidIsoDate(value)) {
      nextParams.delete("start_date");
      nextParams.delete("end_date");
      return nextParams;
    }

    nextParams.set(key, value);

    const nextStartDate = nextParams.get("start_date");
    const nextEndDate = nextParams.get("end_date");

    if (key === "start_date") {
      if (!isValidIsoDate(nextEndDate) || value > nextEndDate) {
        nextParams.set(
          "end_date",
          value > fallbackEndDate ? value : fallbackEndDate,
        );
      }
    }

    if (key === "end_date") {
      if (!isValidIsoDate(nextStartDate) || value < nextStartDate) {
        nextParams.set(
          "start_date",
          value < fallbackStartDate ? value : fallbackStartDate,
        );
      }
    }

    return nextParams;
  });
}

// Only "effective" opts out of the submitted default, mirroring the backend's
// parse so an unknown or absent value resolves to submitted.
function parseDateBasis(value: string | null): StewardshipDateBasis {
  return value === "effective" ? "effective" : "submitted";
}

function resetDateRangeSearchParams(
  setSearchParams: ReturnType<typeof useSearchParams>[1],
): void {
  setSearchParams((currentParams) => {
    const nextParams = new URLSearchParams(currentParams);
    nextParams.delete("start_date");
    nextParams.delete("end_date");
    return nextParams;
  });
}

// The page composes many API-backed cards; keep calculation logic in helpers/backend.
// eslint-disable-next-line complexity
function StewardshipReport(): React.JSX.Element {
  const { id: brokerId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const stewardshipReportingEnabled = useFeatureIsOn(
    CONSULTANT_STEWARDSHIP_REPORTING,
  );
  const showPendingPremiumFallback = useFeatureIsOn(
    CONSULTANT_STEWARDSHIP_PENDING_PREMIUM_FALLBACK,
  );
  const removeIgo = useFeatureIsOn(REMOVE_IGO);
  const rawStartDate = searchParams.get("start_date");
  const rawEndDate = searchParams.get("end_date");
  const hasValidDateRange =
    isValidIsoDate(rawStartDate) &&
    isValidIsoDate(rawEndDate) &&
    rawStartDate <= rawEndDate;
  const startDate = hasValidDateRange ? rawStartDate : undefined;
  const endDate = hasValidDateRange ? rawEndDate : undefined;
  const dateBasis = parseDateBasis(searchParams.get("date_basis"));
  const brokerQuery = useConsultingBroker(brokerId);
  const reportQuery = useBrokerStewardshipQuery(brokerId, {
    enabled: stewardshipReportingEnabled,
    startDate,
    endDate,
    dateBasis,
  });
  // Export uses the currently applied filters (URL state), never the unsaved
  // draft date-picker selection, so the file matches the displayed report.
  const exportStewardship = useExportStewardship({
    brokerId,
    brokerName: brokerQuery.data?.name ?? "",
    startDate,
    endDate,
    dateBasis,
  });
  const downloadPowerPoint = useStewardshipPowerPointDownload();
  const effectiveStartDate = startDate ?? reportQuery.data?.period.start_date;
  const effectiveEndDate = endDate ?? reportQuery.data?.period.end_date;
  const [draftStartDate, setDraftStartDate] = useState<Date | null>(() =>
    parseIsoDate(effectiveStartDate),
  );
  const [draftEndDate, setDraftEndDate] = useState<Date | null>(() =>
    parseIsoDate(effectiveEndDate),
  );
  const [dateRefreshRequested, setDateRefreshRequested] = useState(false);
  const [dateRefreshStarted, setDateRefreshStarted] = useState(false);
  const isRefreshingReport =
    dateRefreshRequested &&
    stewardshipReportingEnabled &&
    reportQuery.isFetching &&
    !reportQuery.isLoading &&
    Boolean(reportQuery.data);

  useEffect(() => {
    setDraftStartDate(parseIsoDate(effectiveStartDate));
    setDraftEndDate(parseIsoDate(effectiveEndDate));
  }, [effectiveStartDate, effectiveEndDate]);

  useEffect(() => {
    if (dateRefreshRequested && reportQuery.isFetching) {
      setDateRefreshStarted(true);
    }

    if (dateRefreshStarted && !reportQuery.isFetching) {
      setDateRefreshRequested(false);
      setDateRefreshStarted(false);
    }
  }, [dateRefreshRequested, dateRefreshStarted, reportQuery.isFetching]);

  if (brokerQuery.isError) {
    return (
      <Box mt={2} mb={2}>
        <Alert severity="error">{extractErrorMessage(brokerQuery.error)}</Alert>
      </Box>
    );
  }

  if (stewardshipReportingEnabled && reportQuery.isError) {
    return (
      <Box mt={2} mb={2}>
        <Alert severity="error">{extractErrorMessage(reportQuery.error)}</Alert>
      </Box>
    );
  }

  if (
    brokerQuery.isLoading ||
    (stewardshipReportingEnabled && reportQuery.isLoading) ||
    !brokerQuery.data
  ) {
    return <Loader />;
  }

  if (!stewardshipReportingEnabled || !reportQuery.data) {
    return (
      <Box mt={8}>
        <SectionHeader
          title={brokerQuery.data.name}
          subtitle="Marketing KPIs"
          onBack={() => navigate(`/${ROUTE_NAMES.CONSULTING}/${brokerId}`)}
        />
        <Box m={3}>
          <Alert severity="info">
            No stewardship report data is available yet.
          </Alert>
        </Box>
      </Box>
    );
  }

  const report = reportQuery.data;
  const metrics = report.metrics;
  const broker = brokerQuery.data;
  const periodLabel = formatPeriodLabel(report);
  const clientPortfolio = metrics.client_portfolio;
  const pendingOutcomes = metrics.pending_outcomes;
  const carrierDetails = metrics.carrier_details ?? [];
  const rfpSubmitterActivity = metrics.rfp_submitter_activity ?? [];

  return (
    <Box mt={8}>
      <SectionHeader
        title={broker.name}
        subtitle={`Marketing KPIs · ${periodLabel}`}
        onBack={() => navigate(`/${ROUTE_NAMES.CONSULTING}/${brokerId}`)}
      />
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", m: 3 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={dateBasis}
          disabled={isRefreshingReport}
          aria-label="Report date basis"
          onChange={(_event, nextBasis: StewardshipDateBasis | null) => {
            // Exclusive groups emit null when the active button is re-clicked;
            // keep the current selection in that case.
            if (nextBasis === null || nextBasis === dateBasis) {
              return;
            }

            setDateRefreshRequested(true);
            setSearchParams((currentParams) => {
              const nextParams = new URLSearchParams(currentParams);

              if (nextBasis === "effective") {
                nextParams.set("date_basis", "effective");
              } else {
                // Drop the param for the submitted default to keep URLs clean.
                nextParams.delete("date_basis");
              }

              return nextParams;
            });
          }}
        >
          <ToggleButton value="submitted">Submitted</ToggleButton>
          <ToggleButton value="effective">Effective</ToggleButton>
        </ToggleButtonGroup>
        <DatePicker
          label="Start date"
          value={draftStartDate}
          format="yyyy-MM-dd"
          disabled={isRefreshingReport}
          onChange={(value) => setDraftStartDate(value)}
          onAccept={(value) => {
            setDateRefreshRequested(true);
            updateDateRangeSearchParam({
              setSearchParams,
              key: "start_date",
              value: formatAcceptedDate(value),
              fallbackStartDate: report.period.start_date,
              fallbackEndDate: report.period.end_date,
            });
          }}
          slotProps={DATE_PICKER_SLOT_PROPS}
        />
        <DatePicker
          label="End date"
          value={draftEndDate}
          format="yyyy-MM-dd"
          disabled={isRefreshingReport}
          onChange={(value) => setDraftEndDate(value)}
          onAccept={(value) => {
            setDateRefreshRequested(true);
            updateDateRangeSearchParam({
              setSearchParams,
              key: "end_date",
              value: formatAcceptedDate(value),
              fallbackStartDate: report.period.start_date,
              fallbackEndDate: report.period.end_date,
            });
          }}
          slotProps={DATE_PICKER_SLOT_PROPS}
        />
        <Button
          variant="secondary"
          disabled={isRefreshingReport}
          onClick={() => {
            setDateRefreshRequested(true);
            setDraftStartDate(parseIsoDate(report.period.start_date));
            setDraftEndDate(parseIsoDate(report.period.end_date));
            resetDateRangeSearchParams(setSearchParams);
          }}
        >
          Reset to YTD
        </Button>
        <Button
          variant="primary"
          data-testid="stewardship-export-button"
          disabled={isRefreshingReport || exportStewardship.isExporting}
          onClick={() => exportStewardship.exportReport()}
        >
          {exportStewardship.isExporting ? "Exporting…" : "Export to Excel"}
        </Button>
        <Button
          variant="primary"
          disabled={
            isRefreshingReport || downloadPowerPoint.isPending || !brokerId
          }
          startIcon={
            downloadPowerPoint.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
          onClick={() => {
            if (!brokerId) {
              return;
            }

            downloadPowerPoint.mutate({
              brokerId,
              startDate: effectiveStartDate,
              endDate: effectiveEndDate,
              dateBasis,
            });
          }}
        >
          Download PowerPoint
        </Button>
      </Box>
      {downloadPowerPoint.isError && (
        <Box sx={{ mx: 3, mt: -1, mb: 3 }}>
          <Alert severity="error">
            Couldn’t generate the PowerPoint. Please try again.
          </Alert>
        </Box>
      )}
      {isRefreshingReport && (
        <Box sx={{ mx: 3, mt: -1, mb: 3 }}>
          <Typography
            variant="body-medium"
            role="status"
            aria-live="polite"
            sx={{ mb: 1 }}
            color="textSecondary"
          >
            Refreshing report…
          </Typography>
          <LinearProgress />
        </Box>
      )}
      <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", m: 3 }}>
        <Box
          sx={{
            ...CARD_SX,
            width: 320,
            minWidth: 320,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <YtdSummaryCard
            marketedPremium={metrics.marketed_premium ?? 0}
            finalizedPremium={metrics.finalized_premium ?? 0}
            premiumOutToMarket={metrics.premium_out_to_market ?? 0}
            migrationSuccessPremium={
              metrics.migration_success?.opportunity_premium ?? 0
            }
            retentionSuccessPremium={
              metrics.retention_success?.opportunity_premium ?? 0
            }
            pendingOutcomesPremium={pendingOutcomes?.total_premium ?? 0}
            hidePendingOutcomesPremium={showPendingPremiumFallback}
            newLinePremium={
              metrics.new_line_premium_breakdown?.opportunity_premium ?? 0
            }
            movedToStrategicPremium={
              clientPortfolio?.moved_to_strategic_premium ?? 0
            }
            retainedWithStrategicPremium={
              clientPortfolio?.retained_with_strategic_premium ?? 0
            }
            strategicSharePercent={
              clientPortfolio?.strategic_share_percent ?? 0
            }
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <KeyMetricsCard
              rfpCount={metrics.rfp_count}
              completedRfpCount={metrics.completed_rfp_count}
              averageDaysToIgo={removeIgo ? undefined : metrics.avg_days_to_igo}
              averageProducts={metrics.avg_products ?? 0}
              averageCarriersQuoted={metrics.avg_carriers_quoted ?? 0}
              plansPresented={metrics.plans_presented ?? 0}
              hoursSaved={metrics.hours_saved ?? 0}
            />
            <MarketingOutcomesCard
              migration={marketingOutcomeFromSuccess(metrics.migration_success)}
              retention={marketingOutcomeFromSuccess(metrics.retention_success)}
              newLine={marketingOutcomeFromSuccess(
                metrics.new_line_premium_breakdown,
              )}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 3 }}>
            <CarrierOutcomesCard
              premiumWon={(metrics.carriers_won ?? []).map((carrier) => ({
                name: carrier.carrier,
                amount: carrier.premium,
                tier: normalizeCarrierTier(carrier.tier),
              }))}
              premiumRetained={(metrics.carriers_retained ?? []).map(
                (carrier) => ({
                  name: carrier.carrier,
                  amount: carrier.premium,
                  tier: normalizeCarrierTier(carrier.tier),
                }),
              )}
            />
            <PendingOutcomesCard
              tier1Premium={pendingOutcomes?.split.tier_1 ?? 0}
              tier2Premium={pendingOutcomes?.split.tier_2 ?? 0}
              nonStrategicPremium={pendingOutcomes?.split.non_strategic ?? 0}
              tier1Count={pendingOutcomes?.count_split.tier_1 ?? 0}
              tier2Count={pendingOutcomes?.count_split.tier_2 ?? 0}
              nonStrategicCount={
                pendingOutcomes?.count_split.non_strategic ?? 0
              }
              pendingRfpCount={pendingOutcomes?.pending_rfp_count ?? 0}
              showGroupCounts={showPendingPremiumFallback}
            />
          </Box>

          <RfpSubmitterActivityCard
            rows={rfpSubmitterActivity}
            showIgoColumns={!removeIgo}
          />

          <CarrierPerformanceCard
            carriers={carrierDetails.map((carrier) => ({
              key:
                carrier.carrier_key ??
                `${carrier.carrier}-${carrier.tier}-${carrier.premium_incumbent}-${carrier.premium_new_revenue}-${carrier.premium_retained}-${carrier.rfp_count}`,
              name: carrier.carrier,
              tier: normalizeCarrierTier(carrier.tier),
              marketedWonPremium: carrier.premium_new_revenue,
              retainedPremium: carrier.premium_retained,
              rfpCount: carrier.rfp_count,
            }))}
          />
        </Box>
      </Box>
    </Box>
  );
}

export { StewardshipReport };
